import { useEffect } from "react";
import {
  useForm,
  useFormContext,
  useWatch,
  FormProvider,
} from "react-hook-form";
import classNames from "classnames";
import TranslatableTextInput from "../../shared/multilingualism/TranslatableTextInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faSquareCheck } from "@fortawesome/pro-solid-svg-icons";
import { faCircle, faSquare } from "@fortawesome/pro-regular-svg-icons";
import { get, isEmpty, map } from "lodash";
import { ApiError } from "../../../helpers/reactQueryApi";
import { ScheduleField } from "../../activitystreams/composer/ScheduleFields";
import type { Answer, ParticipationQuestionnaire, Question } from "../types";
import {
  defaultAnswers,
  optionIds,
  proposalLabel,
  toAnswers,
  type AnswerFields,
} from "./answers";

type FormValues = { answers: AnswerFields[] };

type ParticipationFormProps = {
  questionnaire: ParticipationQuestionnaire;
  // Editor preview: answering works, sending does not
  preview?: boolean;
  onSubmit?: (answers: Answer[]) => Promise<unknown>;
  onCancel?: () => void;
};

const PATTERNS = {
  email: /^[^@\s]+@[^@\s]+$/,
  url: /^https?:\/\/\S+$/i,
  number: /^[+-]?\d+([.,]\d+)?$/,
};

const INPUT_TYPES = { email: "email", url: "url" };

function errorMessage(key: string) {
  return I18n.t(`js.questionnaires.errors.${key}`);
}

export default function ParticipationForm({
  questionnaire,
  preview = false,
  onSubmit,
  onCancel,
}: ParticipationFormProps) {
  const methods = useForm<FormValues>({
    defaultValues: { answers: defaultAnswers(questionnaire) },
  });
  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  async function submit({ answers }: FormValues) {
    if (preview || !onSubmit) return;

    try {
      await onSubmit(toAnswers(questionnaire.questions, answers));
    } catch (error) {
      const errors = (error instanceof ApiError &&
        error.responseJson?.errors) as Record<string, string> | undefined;
      if (!errors) {
        toastr.error(I18n.t("js.saving_failure"));
        return;
      }
      questionnaire.questions.forEach((question, index) => {
        if (errors[question.id]) {
          // on the field the question registers, else the next submit's validation never clears it
          setError(
            question.type === "text"
              ? `answers.${index}.value`
              : `answers.${index}.option_ids`,
            {
              type: "server",
              message: errorMessage(errors[question.id]),
            },
          );
        }
      });
      if (errors.base) toastr.error(errorMessage(errors.base));
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(submit)}
        noValidate
        className="flex flex-col gap-6"
      >
        {map(questionnaire.questions, (question, index) => (
          <QuestionField key={question.id} question={question} index={index} />
        ))}
        <p className="text-sm text-muted">
          {I18n.t("js.questionnaires.participation.required_hint")}
        </p>
        {/* the shared actions row: cancel left of submit, right aligned, stacked on a phone */}
        <div className="tixxt__form--actions">
          {onCancel && (
            <button type="button" className="btn btn-light" onClick={onCancel}>
              {I18n.t("js.cancel")}
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={preview || isSubmitting}
            title={
              preview
                ? I18n.t("js.questionnaires.editor.preview_submit_disabled")
                : undefined
            }
          >
            {I18n.t("js.questionnaires.participation.submit")}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}

function QuestionField({
  question,
  index,
}: {
  question: Question;
  index: number;
}) {
  const {
    formState: { errors },
  } = useFormContext<FormValues>();
  const error =
    get(errors, `answers.${index}.value.message`) ||
    get(errors, `answers.${index}.option_ids.message`);
  const legendId = `question-${question.id}`;

  return (
    <fieldset
      aria-labelledby={legendId}
      className={classNames("flex flex-col gap-2", {
        "field-error field-error-live": error,
      })}
      data-question-id={question.id}
    >
      <legend id={legendId} className="font-semibold text-base mb-1">
        {question.content}
        {question.required && (
          // plain text colour, the reason on hover and for screen readers,
          // which the form does not say otherwise: it validates in JS only
          <>
            <span
              className="ml-1"
              aria-hidden="true"
              title={I18n.t("js.questionnaires.participation.required_marker")}
            >
              *
            </span>
            <span className="sr-only">
              {I18n.t("js.questionnaires.participation.required_marker")}
            </span>
          </>
        )}
      </legend>
      {question.type === "text" ? (
        <TextAnswer question={question} index={index} />
      ) : (
        <ChoiceAnswer question={question} index={index} />
      )}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {String(error)}
        </p>
      )}
    </fieldset>
  );
}

function TextAnswer({
  question,
  index,
}: {
  question: Question;
  index: number;
}) {
  const { register } = useFormContext<FormValues>();
  const format = question.input_format || "text";
  const field = register(`answers.${index}.value`, {
    required: question.required && errorMessage("required"),
    maxLength: { value: 5000, message: errorMessage("too_long") },
    ...(format !== "text" && {
      pattern: {
        value: PATTERNS[format],
        message: errorMessage(`invalid_${format}`),
      },
    }),
  });

  return format === "text" ? (
    <textarea
      className="form-control"
      rows={3}
      aria-label={question.content}
      {...field}
    />
  ) : (
    <input
      className="form-control"
      type={INPUT_TYPES[format] || "text"}
      inputMode={format === "number" ? "decimal" : undefined}
      aria-label={question.content}
      {...field}
    />
  );
}

function ChoiceAnswer({
  question,
  index,
}: {
  question: Question;
  index: number;
}) {
  const { register, setValue, getValues } = useFormContext<FormValues>();
  const choices =
    question.type === "poll"
      ? map(question.options, (option) => ({
          id: option.id,
          label: option.label,
        }))
      : map(question.proposals, (proposal) => ({
          id: proposal.id,
          label: proposalLabel(proposal),
        }));
  const ownAnswer = question.type === "poll" && question.option_adding;
  const ownDate = question.type === "schedule" && question.option_adding;
  const ownDateDay = useWatch<FormValues>({
    name: `answers.${index}.custom_proposal.date`,
  });
  const ownOption = useWatch<FormValues>({
    name: `answers.${index}.custom_option`,
  }) as string | undefined;
  const ownTranslations = useWatch<FormValues>({
    name: `answers.${index}.custom_option_translations`,
  }) as Record<string, Record<string, string>> | undefined;
  const unchecked = question.multiple_select ? faSquare : faCircle;
  const checked = question.multiple_select ? faSquareCheck : faCircleCheck;
  const ownOptionIcon = isEmpty(ownOption?.trim()) ? unchecked : checked;
  const ownDateIcon = isEmpty(ownDateDay) ? unchecked : checked;
  const field = register(`answers.${index}.option_ids`, {
    validate: (value) =>
      !question.required ||
      optionIds(value).length > 0 ||
      !isEmpty(getValues(`answers.${index}.custom_option`)?.trim()) ||
      !isEmpty(getValues(`answers.${index}.custom_proposal.date`)) ||
      errorMessage("required"),
    onChange: () => {
      if (question.multiple_select) return;
      if (ownAnswer) {
        setValue(`answers.${index}.custom_option`, "");
        // the languages belonged to the answer that was just dropped
        setValue(`answers.${index}.custom_option_translations`, {});
      }
      if (ownDate) setValue(`answers.${index}.custom_proposal.date`, null);
    },
  });

  // like the stream's schedule: an own date replaces the choice of a single-select question
  useEffect(() => {
    if (ownDate && !question.multiple_select && !isEmpty(ownDateDay))
      setValue(`answers.${index}.option_ids`, "");
  }, [ownDateDay, ownDate, question.multiple_select, index, setValue]);

  return (
    <div className="flex flex-col gap-1">
      {map(choices, (choice) => (
        <label
          key={choice.id}
          // the stream's survey look, without its result bar: a row to click; the row shows the hidden input's keyboard focus
          className="option-container flex items-center gap-2 py-2 px-3 rounded bg-gray-50 hover:bg-gray-100 has-focus-visible:ring-2 has-focus-visible:ring-primary font-normal cursor-pointer"
        >
          <input
            type={question.multiple_select ? "checkbox" : "radio"}
            value={choice.id}
            className="peer sr-only"
            {...field}
          />
          {/* the span carries the visibility: FontAwesome's own display rule beats "hidden" */}
          <span className="shrink-0 text-muted peer-checked:hidden">
            <FontAwesomeIcon
              icon={question.multiple_select ? faSquare : faCircle}
            />
          </span>
          <span className="hidden shrink-0 text-primary peer-checked:inline-block">
            <FontAwesomeIcon
              icon={question.multiple_select ? faSquareCheck : faCircleCheck}
            />
          </span>
          {choice.label}
        </label>
      ))}
      {ownAnswer && (
        // like the stream's survey: the own answer carries the same mark as the options
        <div className="custom-option-container flex items-center gap-2 px-3">
          <span
            className={classNames("shrink-0", {
              "text-primary": !isEmpty(ownOption?.trim()),
              "text-muted": isEmpty(ownOption?.trim()),
            })}
          >
            <FontAwesomeIcon icon={ownOptionIcon} />
          </span>
          <TranslatableTextInput
            containerClassName="flex-1 min-w-0"
            className="form-control"
            maxLength={250}
            placeholder={I18n.t(
              "js.questionnaires.participation.custom_option",
            )}
            aria-label={I18n.t("js.questionnaires.participation.custom_option")}
            field="label"
            value={ownOption || ""}
            translationsValue={ownTranslations || {}}
            onValueChange={(label, translations) => {
              setValue(`answers.${index}.custom_option`, label);
              // an emptied answer drops its languages, they belonged to the old text
              setValue(
                `answers.${index}.custom_option_translations`,
                label.trim() ? translations : {},
              );
              if (!question.multiple_select && label.trim())
                setValue(`answers.${index}.option_ids`, "");
            }}
          />
        </div>
      )}
      {ownDate && (
        <div className="flex flex-col gap-1 mt-1">
          <span className="text-sm text-muted">
            {I18n.t("js.questionnaires.participation.custom_proposal")}
          </span>
          <div className="custom-option-container flex items-start gap-2 px-3">
            <span
              className={classNames("shrink-0 mt-2", {
                "text-primary": !isEmpty(ownDateDay),
                "text-muted": isEmpty(ownDateDay),
              })}
            >
              <FontAwesomeIcon icon={ownDateIcon} />
            </span>
            <ScheduleField
              dateName={`answers.${index}.custom_proposal.date`}
              timeName={`answers.${index}.custom_proposal.time`}
              timeEndName={`answers.${index}.custom_proposal.time_end`}
              allowEmptyTime
              labels={{
                date: I18n.t("js.questionnaires.editor.date"),
                time: I18n.t("js.questionnaires.editor.starts_at"),
                timeEnd: I18n.t("js.questionnaires.editor.ends_at"),
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
